import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Download, LogOut } from 'lucide-react';
import { can, toDisplayScore } from '@cercle/shared';
import { useApi, useApp } from '@/shared/app-context';
import { useAsync } from '@/shared/hooks/useAsync';
import { Avatar, Button, Card, EmptyState, Field, Gauge, Input, Muted, Skeleton } from '@/shared/ui';
import { AvatarField } from './AvatarField';
import { ChangePasswordCard } from './ChangePasswordCard';
import { useInstallPrompt } from './useInstallPrompt';
import { formatShortDate } from '../session/format';
import styles from './profile.module.scss';

/** Écran 07 — profil : part des anges, palais, état de synchronisation, installation. */
export function ProfileScreen(): ReactNode {
  const api = useApi();
  const { member, signOut, sync } = useApp();
  const stats = useAsync(() => api.members.stats(), []);
  const palate = useAsync(() => api.members.palate(), []);
  const install = useInstallPrompt();

  const [pseudo, setPseudo] = useState(member?.pseudo ?? '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  if (!member) return <Skeleton />;

  async function save(): Promise<void> {
    setSaving(true);
    setSaved(false);
    try {
      await api.members.updateProfile({ pseudo });
      setSaved(true);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={styles.page}>
      <header className={styles.identity}>
        <Avatar pseudo={member.pseudo} url={member.avatarUrl} size="lg" />
        <div>
          <h1>{member.pseudo}</h1>
          <Muted>Membre depuis {formatShortDate(member.createdAt)}</Muted>
        </div>
        <Button variant="ghost" onClick={() => void signOut()}>
          <LogOut size={16} strokeWidth={2.75} aria-hidden="true" /> Se déconnecter
        </Button>
      </header>

      <section>
        <h2 className={styles.sectionTitle}>Votre part des anges</h2>
        {stats.loading ? (
          <Skeleton />
        ) : stats.data ? (
          <dl className={styles.stats}>
            <Stat value={stats.data.sessionsAttended} label="séances honorées" />
            <Stat value={stats.data.tastingsSubmitted} label="notes déposées" />
            <Stat
              value={stats.data.averageGiven === null ? '—' : toDisplayScore(stats.data.averageGiven)}
              label="moyenne donnée"
            />
            <Stat value={stats.data.bestNoseCount} label="meilleurs nez" />
          </dl>
        ) : null}
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Votre palais · par pays</h2>
        {palate.loading ? <Skeleton /> : null}
        {palate.data && palate.data.byCountry.length === 0 ? (
          <EmptyState>Notez quelques rhums et votre palais se dessinera ici.</EmptyState>
        ) : null}
        <ul className={styles.palate}>
          {palate.data?.byCountry.map((entry) => {
            const delta = entry.averageGiven - entry.clubAverage;
            return (
              <li key={entry.label} className={styles.palateRow}>
                <span className={styles.palateHead}>
                  <span>{entry.label}</span>
                  <strong>{toDisplayScore(entry.averageGiven)}</strong>
                </span>
                <Gauge percent={entry.averageGiven} />
                <span className={styles.palateDelta}>
                  {delta === 0
                    ? 'dans la moyenne du club'
                    : `${delta > 0 ? '+' : '−'}${toDisplayScore(Math.abs(delta))} par rapport au club`}
                </span>
              </li>
            );
          })}
        </ul>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Profil</h2>
        <Card className={styles.form}>
          <Field label="Photo de profil">
            <AvatarField />
          </Field>
          <Field label="Pseudo">
            <Input value={pseudo} onChange={(event) => setPseudo(event.target.value)} />
          </Field>
          <Field label="Email" hint="L’email identifie votre compte, il n’est pas modifiable.">
            <Input value={member.email} readOnly disabled />
          </Field>
          <Button disabled={saving || pseudo === member.pseudo} onClick={() => void save()}>
            {saving ? 'Enregistrement…' : saved ? 'Enregistré ✓' : 'Enregistrer'}
          </Button>
        </Card>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Mot de passe</h2>
        <ChangePasswordCard />
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Appareil</h2>
        <Card className={styles.device}>
          <p>
            {sync.pending > 0
              ? `${sync.pending} note${sync.pending > 1 ? 's' : ''} en attente de synchronisation.`
              : 'Tout est synchronisé.'}
          </p>
          {install.installed ? (
            <Muted>Application installée · fonctionne hors-ligne en salle Courbet.</Muted>
          ) : install.canPrompt ? (
            <Button variant="secondary" onClick={() => void install.prompt()}>
              <Download size={16} strokeWidth={2.75} aria-hidden="true" /> Installer l’application
            </Button>
          ) : (
            <Muted>
              Pour l’installer sur iPhone : bouton Partager, puis « Sur l’écran d’accueil ».
            </Muted>
          )}
        </Card>
      </section>

      {can(member.role, 'session:manage') || can(member.role, 'member:manage') ? (
        <section>
          <h2 className={styles.sectionTitle}>Régie</h2>
          <div className={styles.adminLinks}>
            {can(member.role, 'session:manage') ? (
              <>
                <Link to="/regie/seance">Conduire la séance</Link>
                <Link to="/regie/seances">Calendrier des séances</Link>
              </>
            ) : null}
            {can(member.role, 'member:manage') ? (
              <Link to="/regie/membres">Gérer les membres</Link>
            ) : null}
            {can(member.role, 'reference:manage') ? (
              <Link to="/regie/referentiel">Tenir le référentiel</Link>
            ) : null}
          </div>
        </section>
      ) : null}

      <p className={styles.deferred}>
        <Muted>
          Badges, statistiques détaillées et notifications de rappel arrivent avec le lot 3.
        </Muted>
      </p>
    </div>
  );
}

function Stat({ value, label }: { value: number | string; label: string }): ReactNode {
  return (
    <div className={styles.stat}>
      <dt>{value}</dt>
      <dd>{label}</dd>
    </div>
  );
}
